import { useState, useEffect, useRef } from 'react';
import { parrainageAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Save, FileText, Upload, Download, ScrollText, User } from 'lucide-react';
import { toast } from 'sonner';

const blobDownload = (data, filename) => {
    const url = URL.createObjectURL(data);
    const a = document.createElement('a');
    a.href = url; a.download = filename || 'document';
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
};

export function ParrainSettingsTab() {
    const [space, setSpace] = useState(null);
    const [form, setForm] = useState(null);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);

    const load = async () => {
        try {
            const res = await parrainageAPI.getMySpace();
            setSpace(res.data);
            setForm(res.data.profile);
        } catch (e) {
            toast.error('Erreur de chargement');
        }
    };
    useEffect(() => { load(); }, []);

    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const save = async () => {
        setSaving(true);
        try {
            await parrainageAPI.updateMyProfile({
                first_name: form.first_name, last_name: form.last_name, phone: form.phone,
                address: form.address, postal_code: form.postal_code, city: form.city,
                iban: form.iban, bic: form.bic,
            });
            toast.success('Coordonnées enregistrées');
        } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
        finally { setSaving(false); }
    };

    const onUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append('file', file);
            await parrainageAPI.uploadMyContract(fd);
            toast.success('Contrat envoyé');
            load();
        } catch (err) { toast.error(err.response?.data?.detail || 'Erreur lors de l\'envoi'); }
        finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
    };

    const downloadTemplate = async () => {
        try {
            const res = await parrainageAPI.downloadTemplate();
            blobDownload(res.data, space?.contract_template?.filename || 'contrat-parrainage.pdf');
        } catch (e) { toast.error('Aucun modèle disponible'); }
    };
    const downloadMine = async () => {
        try {
            const res = await parrainageAPI.downloadMyContract();
            blobDownload(res.data, space?.my_contract?.filename || 'mon-contrat.pdf');
        } catch (e) { toast.error('Aucun contrat disponible'); }
    };

    if (!form) {
        return <div className="py-12 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" /></div>;
    }

    const field = (key, label, placeholder, type = 'text', readOnly = false) => (
        <div className="space-y-1.5">
            <Label className="text-sm">{label}</Label>
            <Input type={type} value={form[key] || ''} placeholder={placeholder} readOnly={readOnly}
                onChange={e => set(key, e.target.value)} className={readOnly ? 'bg-slate-100' : ''}
                data-testid={`parrain-field-${key}`} />
        </div>
    );

    return (
        <div className="space-y-6" data-testid="parrain-settings-content">
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base"><User className="w-4 h-4" /> Mes coordonnées personnelles</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="grid sm:grid-cols-2 gap-4">
                        {field('first_name', 'Prénom', 'Jean')}
                        {field('last_name', 'Nom', 'Dupont')}
                        {field('email', 'Email (connexion)', '', 'email', true)}
                        {field('phone', 'Téléphone', '06 12 34 56 78')}
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                        <div className="sm:col-span-2">{field('address', 'Adresse', '12 rue des Lilas')}</div>
                        {field('postal_code', 'Code postal', '75001')}
                        {field('city', 'Ville', 'Paris')}
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                        {field('iban', 'IBAN', 'FR76 ...')}
                        {field('bic', 'BIC', 'XXXXXXXX')}
                    </div>
                    <div className="flex justify-end">
                        <Button onClick={save} disabled={saving} data-testid="parrain-coords-save">
                            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                            Enregistrer
                        </Button>
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base"><ScrollText className="w-4 h-4" /> Règlement du parrainage</CardTitle>
                </CardHeader>
                <CardContent>
                    {space?.reglement ? (
                        <div className="prose prose-sm max-w-none text-slate-700 whitespace-pre-wrap" data-testid="parrain-reglement"
                            dangerouslySetInnerHTML={{ __html: space.reglement }} />
                    ) : (
                        <p className="text-sm text-slate-400" data-testid="parrain-reglement-empty">Le règlement sera publié prochainement par Valnexo.</p>
                    )}
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base"><FileText className="w-4 h-4" /> Mon contrat de parrainage</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="flex flex-wrap items-center gap-3">
                        <Button variant="outline" onClick={downloadTemplate} disabled={!space?.contract_template} data-testid="parrain-template-download">
                            <Download className="w-4 h-4 mr-2" /> Télécharger le modèle de contrat
                        </Button>
                        {!space?.contract_template && <span className="text-xs text-slate-400">Modèle non encore disponible</span>}
                    </div>
                    <div className="rounded-lg border border-dashed border-slate-300 p-5 text-center">
                        <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp,.doc,.docx" className="hidden" onChange={onUpload} data-testid="parrain-contract-input" />
                        <Upload className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                        <p className="text-sm text-slate-600 mb-3">Glissez ou sélectionnez votre contrat signé (PDF, image ou Word).</p>
                        <Button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="parrain-contract-upload-btn">
                            {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                            {space?.my_contract ? 'Remplacer mon contrat' : 'Envoyer mon contrat'}
                        </Button>
                        {space?.my_contract && (
                            <div className="mt-4 flex items-center justify-center gap-2 text-sm text-emerald-700">
                                <FileText className="w-4 h-4" />
                                <span>{space.my_contract.filename}</span>
                                <Button variant="ghost" size="sm" onClick={downloadMine} data-testid="parrain-mycontract-download">
                                    <Download className="w-4 h-4" />
                                </Button>
                            </div>
                        )}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}

export default ParrainSettingsTab;
